/**
 * Seven Free Days — OTA-A copy (docs/plans/SEVEN_FREE_DAYS_CRITICAL_PATH.md,
 * Wave 3, decision 10), with the journey rulings of 2026-09-27
 * (docs/plans/SFD_JOURNEY_RULINGS_2026_09_27.md): "Coach Cedar", never bare "Cedar"
 * (R1); the free period named by its length (G, utils/freePeriodNaming.ts); the closed
 * chat says "Start your subscription to continue" (J14); a returning identity reads
 * "You've already had your free trial with Coach Cedar" (J20).
 *
 * Pure copy selection, no React. Every surface that describes the trial reads
 * ONE server field, `time_bound_trial` on /api/user/subscription-status
 * (per-user aware — the owner's B13 walk-through account sees the new copy
 * before the fleet flip). With the field false or absent the legacy strings
 * below are returned byte-for-byte, so an installed build that receives this
 * OTA before store day keeps telling the truth about the store trial it can
 * still start. The jest gate (web/src/utils/__tests__/sevenFreeDaysCopy.test.ts)
 * pins both branches.
 */

import {
  DEFAULT_FREE_PERIOD_DAYS,
  freePeriodAccountLabel,
  freePeriodDateText,
  freePeriodDone,
  freePeriodDrawerTitle,
  freePeriodName,
  freePeriodProgressSteps,
  freePeriodRunsUntil,
  isFreeMonth,
  normalizeFreePeriodDays,
  startCardTitle,
} from './freePeriodNaming';

export interface SevenFreeDaysFlags {
  /** `data.time_bound_trial === true` from subscription-status. */
  timeBoundTrial: boolean;
  /**
   * `data.lapsed_paid === true` from subscription-status (store-day prep,
   * 2026-09-26): an expired user who once PAID. Owner rule: a paid subscriber
   * whose term ended reads "Re-subscribe to continue with your coach", never
   * "Your 7 days with Coach Cedar are done" (a finished free week) — in either
   * `time_bound_trial` world. Absent/false → the expired copy above, unchanged.
   */
  lapsedPaid?: boolean;
  /**
   * `data.trial_length_days` (journey rulings 2026-09-27, section G): the free period's
   * length — 7, or 14 / 30 with a partner code. The clock-mode words name the period by
   * it ("free week", "2 free weeks", "free month"). Absent → 7: the constants below.
   */
  lengthDays?: number | null;
  /**
   * `data.free_trial_used === true` (journey ruling J20, 2026-09-27): a returning
   * identity — it already had its free period on an earlier account. Its pre-trial
   * surfaces say "You've already had your free trial with Coach Cedar. Start your
   * subscription to continue."
   */
  freeTrialUsed?: boolean;
}

/** The owner's sentence for a paid subscriber whose term ended (2026-09-26). */
export const LAPSED_PAID_HEADLINE = 'Re-subscribe to continue with your coach';

/** J20 (2026-09-27): a returning identity — its free period was on an earlier account. */
export const RETURNING_USER_LINE =
  "You've already had your free trial with Coach Cedar. Start your subscription to continue.";

/** The free period's length, as the copy reads it (7 unless the server named another). */
function periodDays(f: Pick<SevenFreeDaysFlags, 'lengthDays'> | undefined): number {
  return normalizeFreePeriodDays(f?.lengthDays ?? DEFAULT_FREE_PERIOD_DAYS);
}

// ── Chat: the expired dead-composer strip (ChatScreen.native.tsx) ─────────────

export interface StripCopy {
  text: string;
  cta: string;
}

export const CHAT_EXPIRED_STRIP: Readonly<{ legacy: StripCopy; clock: StripCopy; lapsedPaid: StripCopy }> = {
  legacy: { text: 'Your trial has ended. Subscribe to continue.', cta: 'See plans' },
  // J14 (2026-09-27): the paywall was dismissed and the chat is closed.
  clock: { text: 'Your 7 days with Coach Cedar are done. Start your subscription to continue.', cta: 'Subscribe' },
  // J18 (#4119 review M2): the approved words, exactly.
  lapsedPaid: { text: 'Your Coach Cedar subscription has ended.', cta: 'Re-subscribe' },
};

export function expiredStripCopy(f: SevenFreeDaysFlags): StripCopy {
  if (f.lapsedPaid) return CHAT_EXPIRED_STRIP.lapsedPaid;
  if (!f.timeBoundTrial) return CHAT_EXPIRED_STRIP.legacy;
  const days = periodDays(f);
  if (days === DEFAULT_FREE_PERIOD_DAYS) return CHAT_EXPIRED_STRIP.clock;
  return { text: `${freePeriodDone(days)}. Start your subscription to continue.`, cta: 'Subscribe' };
}

/** The strip on the closed pre-trial composer (the trial gate was shown and dismissed). */
export const CHAT_PRE_TRIAL_STRIP: Readonly<{ legacy: StripCopy; returning: StripCopy }> = {
  legacy: { text: 'Start your 7-day trial to continue the conversation.', cta: 'Start 7-day trial' },
  // J20: a returning identity cannot start a free period again.
  returning: { text: RETURNING_USER_LINE, cta: 'Subscribe' },
};

export function preTrialStripCopy(f: Pick<SevenFreeDaysFlags, 'freeTrialUsed'>): StripCopy {
  return f.freeTrialUsed ? CHAT_PRE_TRIAL_STRIP.returning : CHAT_PRE_TRIAL_STRIP.legacy;
}

// ── Account (SettingsScreen.native.tsx subscription block) ───────────────────

export type AccountCtaAction = 'paywall' | 'chat';

export interface AccountSurfaceCopy {
  oneLiner: string;
  ctaLabel: string;
  ctaA11y: string;
  /** 'chat' — nothing to buy yet; the week starts with the first plan. */
  ctaAction: AccountCtaAction;
}

export const ACCOUNT_PRE_TRIAL: Readonly<{ legacy: AccountSurfaceCopy; clock: AccountSurfaceCopy; returning: AccountSurfaceCopy }> = {
  legacy: {
    oneLiner: 'Start your free 7-day trial',
    ctaLabel: 'Start Free Trial',
    ctaA11y: 'Start free trial',
    ctaAction: 'paywall',
  },
  clock: {
    oneLiner: 'Your 7 free days start with your first plan',
    ctaLabel: 'Open chat',
    ctaA11y: 'Open chat to set your first goal',
    ctaAction: 'chat',
  },
  returning: {
    oneLiner: RETURNING_USER_LINE,
    ctaLabel: 'Subscribe',
    ctaA11y: 'Subscribe to Coach Cedar',
    ctaAction: 'paywall',
  },
};

export const ACCOUNT_EXPIRED: Readonly<{
  legacy: AccountSurfaceCopy;
  clock: AccountSurfaceCopy;
  lapsedPaid: AccountSurfaceCopy;
}> = {
  legacy: {
    oneLiner: 'Your trial has ended',
    ctaLabel: 'See Plans',
    ctaA11y: 'See subscription plans',
    ctaAction: 'paywall',
  },
  clock: {
    oneLiner: 'Your 7 days with Coach Cedar are done',
    ctaLabel: 'Continue with Coach Cedar',
    ctaA11y: 'Continue with Coach Cedar',
    ctaAction: 'paywall',
  },
  lapsedPaid: {
    oneLiner: LAPSED_PAID_HEADLINE,
    ctaLabel: 'Re-subscribe',
    ctaA11y: 'Re-subscribe to Coach Cedar',
    ctaAction: 'paywall',
  },
};

export function accountPreTrialCopy(f: SevenFreeDaysFlags): AccountSurfaceCopy {
  if (f.freeTrialUsed) return ACCOUNT_PRE_TRIAL.returning;
  if (!f.timeBoundTrial) return ACCOUNT_PRE_TRIAL.legacy;
  const days = periodDays(f);
  if (days === DEFAULT_FREE_PERIOD_DAYS) return ACCOUNT_PRE_TRIAL.clock;
  const oneLiner = isFreeMonth(days)
    ? 'Your free month starts with your first plan'
    : `Your ${days} free days start with your first plan`;
  return { ...ACCOUNT_PRE_TRIAL.clock, oneLiner };
}

export function accountExpiredCopy(f: SevenFreeDaysFlags): AccountSurfaceCopy {
  if (f.lapsedPaid) return ACCOUNT_EXPIRED.lapsedPaid;
  if (!f.timeBoundTrial) return ACCOUNT_EXPIRED.legacy;
  const days = periodDays(f);
  if (days === DEFAULT_FREE_PERIOD_DAYS) return ACCOUNT_EXPIRED.clock;
  return { ...ACCOUNT_EXPIRED.clock, oneLiner: freePeriodDone(days) };
}

/**
 * J13 (2026-09-27): Account's one-liner during a Cedar clock week — "Free week · 5 days
 * left", "Free week · last day", "Free month · 23 days left" (utils/freePeriodNaming).
 */
export function accountActiveClockOneLiner(lengthDays: number | null | undefined, daysLeft: number | null): string {
  return freePeriodAccountLabel(normalizeFreePeriodDays(lengthDays ?? DEFAULT_FREE_PERIOD_DAYS), daysLeft);
}

// ── Native subscription drawer (components/native/drawers/SubscriptionDrawer) ─

export interface DrawerExpiredCopy {
  planName: string;
  badge: string;
  message: string;
  cta: string;
}

export const DRAWER_EXPIRED: Readonly<{
  legacy: DrawerExpiredCopy;
  clock: DrawerExpiredCopy;
  lapsedPaid: DrawerExpiredCopy;
}> = {
  legacy: {
    planName: 'Trial Expired',
    badge: 'Expired',
    message: 'Your free trial has ended. Subscribe to keep using Coach Cedar.',
    cta: 'Subscribe Now',
  },
  clock: {
    planName: 'Your 7 days are done',
    badge: 'Paused',
    message:
      'Your 7 days with Coach Cedar are done. Continue with Coach Cedar to pick up where ' +
      'you left off — your plan and history are saved.',
    cta: 'Continue with Coach Cedar',
  },
  // J18 (#4119 review M2): the approved words, exactly.
  lapsedPaid: {
    planName: 'Subscription ended',
    badge: 'Ended',
    message:
      'Re-subscribe to continue with your coach. Your plan, history and everything ' +
      'Coach Cedar knows about you are saved.',
    cta: 'Re-subscribe',
  },
};

export function drawerExpiredCopy(f: SevenFreeDaysFlags): DrawerExpiredCopy {
  if (f.lapsedPaid) return DRAWER_EXPIRED.lapsedPaid;
  if (!f.timeBoundTrial) return DRAWER_EXPIRED.legacy;
  const days = periodDays(f);
  if (days === DEFAULT_FREE_PERIOD_DAYS) return DRAWER_EXPIRED.clock;
  return {
    ...DRAWER_EXPIRED.clock,
    planName: isFreeMonth(days) ? 'Your free month is done' : `Your ${days} days are done`,
    message:
      `${freePeriodDone(days)}. Continue with Coach Cedar to pick up where ` +
      'you left off — your plan and history are saved.',
  };
}

export interface DrawerPreTrialCopy {
  title: string;
  body: string;
  bulletsTitle: string;
  cta: string;
}

/** Clock-only cards. The legacy pre-trial / active-trial JSX is untouched. */
export const DRAWER_CLOCK_PRE_TRIAL: Readonly<DrawerPreTrialCopy> = {
  title: '7 free days, starting with your first plan',
  body:
    'Chat with Coach Cedar and confirm your first goal. Your 7 free days begin ' +
    'the moment your first plan is built — no card, no charge.',
  bulletsTitle: 'Everything included in your 7 days:',
  cta: 'Open chat',
};

/** The pre-trial clock card for a free period of `lengthDays` (G): 7 → the constant. */
export function drawerClockPreTrialCopy(lengthDays?: number | null): DrawerPreTrialCopy {
  const days = periodDays({ lengthDays });
  if (days === DEFAULT_FREE_PERIOD_DAYS) return DRAWER_CLOCK_PRE_TRIAL;
  if (isFreeMonth(days)) {
    return {
      ...DRAWER_CLOCK_PRE_TRIAL,
      title: 'A free month, starting with your first plan',
      body:
        'Chat with Coach Cedar and confirm your first goal. Your free month begins ' +
        'the moment your first plan is built — no card, no charge.',
      bulletsTitle: 'Everything included in your free month:',
    };
  }
  return {
    ...DRAWER_CLOCK_PRE_TRIAL,
    title: `${days} free days, starting with your first plan`,
    body:
      `Chat with Coach Cedar and confirm your first goal. Your ${days} free days begin ` +
      'the moment your first plan is built — no card, no charge.',
    bulletsTitle: `Everything included in your ${days} days:`,
  };
}

/**
 * J9 (approved 2026-09-27): the price after the free period, from the store at runtime —
 * never typed. null when the store price has not loaded (the line is then left out).
 */
export function drawerPreTrialPriceLine(lengthDays: number | null | undefined, essentialsPrice: string | null | undefined): string | null {
  if (!essentialsPrice) return null;
  const days = periodDays({ lengthDays });
  const after = isFreeMonth(days) ? 'After your free month' : `After your ${days} days`;
  return `${after}, continue from ${essentialsPrice}/month. Nothing is charged unless you choose a plan.`;
}

/** J20: the drawer card for a returning identity (its free period was on an earlier account). */
export const DRAWER_RETURNING = {
  title: "You've already had your free trial with Coach Cedar.",
  body: 'Start your subscription to continue.',
  cta: 'Subscribe',
} as const;

export interface DrawerActiveCopy {
  planName: string;
  badge: string;
  endsPrefix: string;
  body1: string;
  body2: string;
  cta: string;
}

export const DRAWER_CLOCK_ACTIVE_TRIAL: Readonly<DrawerActiveCopy> = {
  planName: 'Your 7 free days with Coach Cedar',
  badge: 'Active',
  endsPrefix: 'Ends: ',
  body1:
    'Right now you have the full Coach Cedar coaching experience at the Chat+ ' +
    'level — open chat, photo and document sharing, and pushing workouts to ' +
    'your watch.',
  body2:
    'When your 7 days are done, Coach Cedar pauses. Your plan, workouts and history ' +
    'stay right here. Continue with Coach Cedar any time to pick up where you left off.',
  cta: 'See plans',
};

/** The in-progress clock card for a free period of `lengthDays` (G, J13): 7 → the constant. */
export function drawerClockActiveCopy(lengthDays?: number | null): DrawerActiveCopy {
  const days = periodDays({ lengthDays });
  if (days === DEFAULT_FREE_PERIOD_DAYS) return DRAWER_CLOCK_ACTIVE_TRIAL;
  const done = isFreeMonth(days) ? 'When your free month is done' : `When your ${days} days are done`;
  return {
    ...DRAWER_CLOCK_ACTIVE_TRIAL,
    planName: freePeriodDrawerTitle(days),
    body2:
      `${done}, Coach Cedar pauses. Your plan, workouts and history ` +
      'stay right here. Continue with Coach Cedar any time to pick up where you left off.',
  };
}

/**
 * J13: the drawer's countdown line — "5 days left · ends Tue, Oct 6", "1 day left · ends …",
 * "Last day · ends …". The end date alone when the count is unknown.
 */
export function drawerDaysLeftLine(daysLeft: number | null, endDateText: string | null): string | null {
  const ends = endDateText ? `ends ${endDateText}` : null;
  if (typeof daysLeft !== 'number' || !Number.isFinite(daysLeft)) return ends ? `${ends.charAt(0).toUpperCase()}${ends.slice(1)}` : null;
  const left = Math.floor(daysLeft);
  const head = left <= 0 ? 'Last day' : `${left} day${left === 1 ? '' : 's'} left`;
  return ends ? `${head} · ${ends}` : head;
}

/**
 * J13 / G: the drawer's progress bar. One step per day up to 14 days (`steps` — `filled`
 * of them done), a continuous bar beyond (`steps` null; `fraction` 0…1). Days done =
 * the length minus the days left (the start day reads 0 done, the last day all done).
 */
export function freePeriodProgress(
  lengthDays: number | null | undefined,
  daysLeft: number | null,
): { steps: number | null; filled: number; fraction: number } {
  const days = periodDays({ lengthDays });
  const left = typeof daysLeft === 'number' && Number.isFinite(daysLeft) ? Math.max(0, Math.floor(daysLeft)) : days;
  const done = Math.min(days, Math.max(0, days - left));
  return { steps: freePeriodProgressSteps(days), filled: done, fraction: done / days };
}

/** J13: "After your free week: continue from {Essentials price}/month." null without a store price. */
export function drawerActivePriceLine(lengthDays: number | null | undefined, essentialsPrice: string | null | undefined): string | null {
  if (!essentialsPrice) return null;
  return `After your ${freePeriodName(periodDays({ lengthDays }))}: continue from ${essentialsPrice}/month.`;
}

// ── Paywall (PaywallModal.tsx), expired hero only ─────────────────────────────

export interface PaywallExpiredCopy {
  title: string;
  subtitle: string;
  cta: string;
  cardCta: string;
}

export const PAYWALL_EXPIRED: Readonly<{
  legacy: PaywallExpiredCopy;
  clock: PaywallExpiredCopy;
  lapsedPaid: PaywallExpiredCopy;
}> = {
  legacy: {
    title: 'Your trial has ended',
    subtitle: 'Subscribe to continue with Coach Cedar',
    cta: 'Subscribe Now',
    cardCta: 'Subscribe',
  },
  clock: {
    title: 'Your 7 days with Coach Cedar are done',
    subtitle: 'Continue with Coach Cedar to pick up where you left off',
    cta: 'Continue with Coach Cedar',
    cardCta: 'Continue',
  },
  lapsedPaid: {
    title: LAPSED_PAID_HEADLINE,
    subtitle: 'Your subscription has ended. Pick up where you left off.',
    cta: 'Re-subscribe',
    cardCta: 'Re-subscribe',
  },
};

/**
 * The expired hero while the paywall's own subscription status is still loading or
 * could not be loaded (review of #4062): nothing yet says whether this account's
 * 7 days ran out, a store trial ended or a subscription lapsed — so it says none of
 * them. Replaced by the real copy the moment the status arrives.
 */
export const PAYWALL_EXPIRED_STATUS_UNKNOWN: PaywallExpiredCopy = {
  title: 'Choose your plan',
  subtitle: 'Pick a plan to keep coaching with Coach Cedar',
  cta: 'Subscribe',
  cardCta: 'Subscribe',
};

/**
 * The legacy paywall header's subtitle for a trial still running (G): the free period's
 * length, not a hard 7. A store trial keeps "7 days" (the store's own trial).
 */
export function paywallActiveTrialSubtitle(f: Pick<SevenFreeDaysFlags, 'timeBoundTrial' | 'lengthDays'>): string {
  if (!f.timeBoundTrial) return 'Enjoying CC Chat+ free for 7 days';
  const days = periodDays(f);
  return `Enjoying CC Chat+ free for ${isFreeMonth(days) ? 'a month' : `${days} days`}`;
}

export function paywallExpiredCopy(f: SevenFreeDaysFlags): PaywallExpiredCopy {
  if (f.lapsedPaid) return PAYWALL_EXPIRED.lapsedPaid;
  if (!f.timeBoundTrial) return PAYWALL_EXPIRED.legacy;
  const days = periodDays(f);
  if (days === DEFAULT_FREE_PERIOD_DAYS) return PAYWALL_EXPIRED.clock;
  return { ...PAYWALL_EXPIRED.clock, title: freePeriodDone(days) };
}

// ── Paywall: the expired context card under the hero (PaywallModal.tsx) ─────

export interface PaywallExpiredContextCopy {
  title: string;
  body: string;
}

export const PAYWALL_EXPIRED_CONTEXT: Readonly<{
  legacy: PaywallExpiredContextCopy;
  lapsedPaid: null;
}> = {
  legacy: {
    title: 'Your trial has ended',
    body: 'Your plans, memories, and progress are saved. Subscribe to pick up right where you left off with Coach Cedar.',
  },
  // A lapsed PAID subscriber (review of #3967): the hero above already says "Re-subscribe
  // to continue with your coach"; a card that says "Your trial has ended" under it is
  // false for someone who paid. No card.
  lapsedPaid: null,
};

/** The expired context card, or null when there is none to show. */
export function paywallExpiredContextCopy(f: SevenFreeDaysFlags): PaywallExpiredContextCopy | null {
  return f.lapsedPaid ? PAYWALL_EXPIRED_CONTEXT.lapsedPaid : PAYWALL_EXPIRED_CONTEXT.legacy;
}

/**
 * The Profile drawer's "Subscription: …" label for an expired user (follow-up to #3967).
 * The words are the Subscription drawer's own card title for the same person,
 * so the two screens never disagree:
 *   - a lapsed payer's subscription ended (DRAWER_EXPIRED.lapsedPaid.planName) —
 *     never "Trial completed", which describes a free week;
 *   - a finished Cedar clock week (`timeBoundTrial`, the server's time_bound_trial)
 *     reads the owner's "your 7 days are done" (the drawer's clock title) —
 *     never "Trial completed", which reads like a store trial;
 *   - everyone else (a store trial's end, the clock off) keeps "Trial completed".
 */
/**
 * The Profile drawer's "Subscription:" label while the free period runs (#4119 review L7):
 * the period's name ("Free week", "2 free weeks", "Free month") for a Cedar clock period;
 * a store trial keeps "Trial".
 */
export function activeProfileLabel(f: Partial<SevenFreeDaysFlags>): string {
  if (!f.timeBoundTrial) return 'Trial';
  const name = freePeriodName(periodDays({ lengthDays: f.lengthDays }));
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function expiredProfileLabel(f: Partial<SevenFreeDaysFlags>): string {
  if (f.lapsedPaid) return DRAWER_EXPIRED.lapsedPaid.planName;
  return f.timeBoundTrial
    ? drawerExpiredCopy({ timeBoundTrial: true, lengthDays: f.lengthDays }).planName
    : 'Trial completed';
}

// ── J14 (2026-09-27): the plan / Move page after a free period ended ─────────

/**
 * The note on the plan page once a Cedar clock free period has lapsed: "Your 7 days with
 * Coach Cedar are done. Subscribe to pick up your plan where you left off." — named by
 * the period's length. Tapping it opens the paywall.
 */
export function planPageLapsedNote(lengthDays?: number | null): { text: string; cta: string } {
  return {
    text: `${freePeriodDone(periodDays({ lengthDays }))}. Subscribe to pick up your plan where you left off.`,
    cta: 'Subscribe',
  };
}

/**
 * Whether the plan page shows that note: a finished Cedar clock free period
 * (`timeBoundTrial` and the expired cohort), never a lapsed payer (their words are
 * "Re-subscribe…") and never a free month that ended (its own copy covers it).
 */
export function showPlanPageLapsedNote(opts: {
  cohort: string | null | undefined;
  timeBoundTrial: boolean;
  lapsedPaid?: boolean;
  freeMonthEnded?: boolean;
}): boolean {
  return opts.cohort === 'subscription_expired' && opts.timeBoundTrial && !opts.lapsedPaid && !opts.freeMonthEnded;
}

// ── J11 (2026-09-27): the start card — a card in chat, never Coach Cedar speaking ──

export interface FreePeriodStartCardCopy {
  title: string;
  body: string;
  small: string;
}

/**
 * The card on the turn that started the free period (goal confirm). Named by the
 * period's length; `endDateText` is "Tue, Oct 6" (utils/freePeriodNaming
 * freePeriodDateText) — without it the sentence names no date.
 */
export function freePeriodStartCard(lengthDays: number | null | undefined, endDateText: string | null): FreePeriodStartCardCopy {
  const days = periodDays({ lengthDays });
  const when = endDateText
    ? `${freePeriodRunsUntil(days, endDateText)}; after that, you choose the plan that suits you.`
    : 'When it ends, you choose the plan that suits you.';
  return {
    title: startCardTitle(days),
    body:
      'You have full access to Coach Cedar, at the Chat+ level. No card, no commitment. ' +
      'Your first plan is being built and will be ready in a few minutes. ' + when,
    small: 'See your days left and explore the plans in Account.',
  };
}

/**
 * Whether a chat message carries the start card: the goal-confirm turn that started the
 * clock (metadata `trialClockStarted`, services/streaming_response_generator.py), with its
 * length and end date when the server sent them.
 */
export function startCardFromMessage(
  metadata: { trialClockStarted?: unknown; trialClockLengthDays?: unknown; trialClockEndsAt?: unknown } | null | undefined,
  fallback?: { lengthDays?: number | null; endsAt?: Date | null },
): FreePeriodStartCardCopy | null {
  if (metadata?.trialClockStarted !== true) return null;
  const length = typeof metadata.trialClockLengthDays === 'number' ? metadata.trialClockLengthDays : fallback?.lengthDays ?? null;
  let ends: Date | null = null;
  if (typeof metadata.trialClockEndsAt === 'string') {
    const d = new Date(metadata.trialClockEndsAt);
    ends = Number.isNaN(d.getTime()) ? null : d;
  }
  if (!ends && fallback?.endsAt instanceof Date) ends = fallback.endsAt;
  return freePeriodStartCard(length, freePeriodDateText(ends));
}

/**
 * Whether a free month that ended (or was revoked) still names the user's state
 * (review of #3967). It never masks a later payment: a lapsed payer
 * (`lapsedPaid`) reads the re-subscribe copy, and a user now in a trial or a paid
 * state reads that state. Mirrors services/revenuecat_attributes.resolve_cedar_cohort.
 */
export function freeMonthEndedStillApplies(cohort: string | null | undefined, lapsedPaid: boolean | undefined): boolean {
  if (lapsedPaid) return false;
  return !['trial_active', 'paid_active', 'paid_cancelled_grace', 'billing_issue'].includes(cohort ?? '');
}

// ── Purchase truth: does this package start a STORE trial? ───────────────────

/**
 * The legacy paywall told the backend `is_trial: true` on every purchase from
 * the trial-mode CTA, because the Essentials product carried Apple's 7-day
 * introductory offer. On store day that offer ends (decision 1) and a purchase
 * charges at once; claiming a trial would park the row in a phantom
 * `status='trial'` until the RevenueCat webhook corrects it. Read the store's
 * own answer instead: a package starts a store trial only when it carries a
 * FREE introductory price. Absent typings or a paid intro → false.
 */
export function packageStartsStoreTrial(
  pkg: { product?: { introPrice?: { price?: unknown } | null } | null } | null | undefined,
): boolean {
  const intro = pkg?.product?.introPrice;
  if (intro == null) return false;
  if (typeof intro.price === 'number') return intro.price === 0;
  return true;
}
